<template>
  <article
    :id="productId"
    class="product-container def-product-container"
    :class="{ 'is-disabled': isLocked }"
    :aria-labelledby="productTitleId"
    :aria-disabled="isLocked ? 'true' : null"
    @click.stop="onContainerClick"
  >
    <div v-if="imageSrc">
      <img class="product-image" :src="imageSrc" :alt="productName" />
    </div>
    <div class="product-info">
      <span :id="productTitleId" class="product-text">{{ productName }}</span>
      <Button secondary
        class="product-details-icon"
        preset-size
        :disabled="isLocked"
        @click.stop="viewProductDetails(productId, isLocked, productName)"
        aria-label="View product details"
        icon-left="info"
        :icon-only="true"
      />

      <div class="product-properties-row">
        <img v-for="property in productProperties"
             :key="property.id"
             :src="property.image"
             :alt="property.name"
             :title="property.name"
             class="product-property-image" />
        <img v-for="allergen in productAllergens"
             :key="allergen.id"
             :src="allergen.image"
             :alt="allergen.name"
             :title="allergen.name"
             class="product-property-image" />
      </div>

      <span class="product-price" v-if="product.formattedPrice">{{ product.formattedPrice }}</span>

      <div id="btn-product-spinner" class="product-spinbutton">
        <SpinButton
          :min="0"
          :modelValue="productCount"
          @update:modelValue="({ value, type }) => handleProductUpdate(productId, isLocked, product.hasChildren, productName, type, value)"
          :hide-decrement="product.hasChildren"
          aria-label="Add or remove product from basket"
          :tabindex="isLocked ? -1 : 0"
          :aria-disabled="isLocked ? 'true' : null"
          @edit="openBasketProductEditor(productId, productName, isLocked)"
          :max="spinButtonMax"
        />
      </div>
    </div>
  </article>
</template>

<script>
import SpinButton from "@/components/page-components/templates/SpinButton.vue";
import Button from "@/components/actions/Button.vue";
import { getProductCountInGroup } from "@/utils/productCountUtils";

export default {
  name: "ProductContainer",
  components: {
    Button,
    SpinButton,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    basketItems: {
      type: Array,
      required: false,
      default: () => []
    },
    groupId: {
      type: [String, Number],
      required: false,
      default: null
    },
    groupMax: {
      type: Number,
      required: false,
      default: 100
    },
    groupCount: {
      type: Number,
      required: false,
      default: 0
    }
  },
  computed: {
    productId() {
      return this.product?.id;
    },
    productName() {
      return this.product?.name || "";
    },
    productTitleId() {
      return this.productId ? `product-${this.productId}-title` : null;
    },
    imageSrc() {
      //return this.product?.image || this.product?.image1 || process.env.BASE_URL + 'settings/resources/logos/logo.png';
      return this.product?.image || this.product?.image1 || "";
    },
    productProperties() {
      return Array.isArray(this.product?.properties) ? this.product.properties : [];
    },
    productAllergens() {
      return Array.isArray(this.product?.allergens) ? this.product.allergens : [];
    },
    isLocked() {
      return !!(this.product?.isLocked || this.product?.locked);
    },
    productCount() {
      if (this.groupId != null) {
        return getProductCountInGroup(this.basketItems, this.groupId, this.productId);
      } else {
        // Flat count for all basketItems with product.id
        return this.basketItems.filter(item => item.prodId === this.productId)
          .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      }
    },
    spinButtonMax() {
      if (this.groupMax != null && this.groupCount != null) {
        return this.productCount + (this.groupMax - this.groupCount);
      }
      return 100;
    }
  },
  methods: {
    viewProductDetails(productId, isLocked, productName) {
      if (isLocked) {
        return;
      }
      this.$emit('view-details', { productId, isLocked, productName, groupId: this.groupId });
    },
    handleProductUpdate(productId, isLocked, hasChildren, productName, type, value) {
      if (isLocked) {
        return;
      }
      // Calculate difference between new value and current productCount
      const diff = value - this.productCount;
      if (diff === 0) return; // No change
      if (diff > 0) {
        // Add only the difference
        this.$emit('update-product', { productId, isLocked, hasChildren, productName, type: 'increment', value: diff, groupId: this.groupId });
      } else {
        // Remove only the difference
        this.$emit('update-product', { productId, isLocked, hasChildren, productName, type: 'decrement', value: Math.abs(diff), groupId: this.groupId });
      }
    },
    openBasketProductEditor(productId, productName, isLocked) {
      if (isLocked) {
        return;
      }
      this.$emit('edit-product', { productId, productName, isLocked, groupId: this.groupId });
    },
    shouldIgnoreNestedInteractive(event) {
      if (!event || !event.target || event.target === event.currentTarget) {
        return false;
      }
      if (typeof event.target.closest !== 'function') {
        return false;
      }

      const nestedInteractive = event.target.closest(
        'a, button, input, select, textarea, summary, [role="button"], [role="link"], [role="checkbox"], [role="menuitem"], [role="option"], [role="radio"], [role="spinbutton"], [role="switch"], [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
      );

      return !!nestedInteractive && nestedInteractive !== event.currentTarget;
    },
    onContainerClick(event) {
      if (this.isLocked || this.shouldIgnoreNestedInteractive(event)) {
        return;
      }
      this.viewProductDetails(this.productId, this.isLocked, this.productName);
    }
  },
};
</script>

<style scoped>
.product-container {
  display: flex;
  flex-direction: column;
  gap: var(--product-container-gap);
  height: 100%;
  position: relative;
  overflow: hidden;
}

.product-info {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  min-width: 0;
  position: relative;
  gap: var(--product-container-gap);
}

.product-text {
  flex: 1 1 auto;
  word-break: break-word;
  min-width: 0;
  margin: 0;
}

.product-image {
  width: 100%;
  object-fit: cover;
  display: block;
}

.product-details-icon {
  position: absolute;
  top: 0;
  right: 0;
  border-radius: 50%;
  margin-top: 0;
}

.product-properties-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--product-container-property-icon-gap);
}

.product-property-image {
  flex: 0 0 auto;
  vertical-align: middle;
}

.product-spinbutton {
  align-self: stretch;
  width: 100%;
}
</style>
